import * as React from "react";
import { Form, FieldRenderProps } from "@progress/kendo-react-form";
import { Input } from "@progress/kendo-react-inputs";
import { EditDescriptor } from "@progress/kendo-react-data-tools";
import {
  Grid,
  GridColumn,
  GridToolbar,
  GridCustomCellProps,
  GridCustomRowProps,
} from "@progress/kendo-react-grid";
import { callApi, HttpMethod } from "src/services/apiService";
import { useCallback, useContext, useEffect, useState } from "react";

// Interfaces for tag data
interface TagData {
  id?: number | null;
  tag: string;
}

interface ApiResponse {
  type: "success" | "error" | "info" | "warning";
  message: string;
  data?: any;
}

const DATA_ITEM_KEY = "id";

const requiredValidator = (value: string) => {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return "The tag name is required";
  }
  return "";
};

const TextInputWithValidation = (fieldRenderProps: FieldRenderProps) => {
  const { validationMessage, visited, ...others } = fieldRenderProps;
  return (
    <div>
      <Input {...others} />
      {visited && validationMessage && (
        <div role="alert" className="k-form-error k-text-start">
          {validationMessage}
        </div>
      )}
    </div>
  );
};

const FormSubmitContext = React.createContext<
  (event: React.SyntheticEvent<any, Event>) => void
>(() => undefined);
const GridEditContext = React.createContext<{
  onDataAction: (options: {
    operation: "save" | "remove" | "add";
    dataItem?: any;
  }) => void;
  setEdit: (edit: EditDescriptor) => void;
  edit: EditDescriptor;
  isLoading: boolean;
  errorMessage: string;
  successMessage: string;
  setTags: React.Dispatch<React.SetStateAction<TagData[]>>;
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  loadTags: () => Promise<void>;
}>({} as any);

const GridInlineFormRow = (props: {
  children: any;
  dataItem: any;
  isInEdit: boolean;
}) => {
  const { onDataAction } = useContext(GridEditContext);
  const onSubmit = useCallback(
    (dataItem: { [name: string]: any }) => {
      onDataAction({ operation: "save", dataItem });
    },
    [onDataAction],
  );

  if (props.isInEdit) {
    return (
      <Form
        key={JSON.stringify(props.dataItem)}
        initialValues={props.dataItem}
        onSubmit={onSubmit}
        render={(formRenderProps) => {
          return (
            <FormSubmitContext.Provider value={formRenderProps.onSubmit}>
              {props.children}
            </FormSubmitContext.Provider>
          );
        }}
      />
    );
  }
  return props.children;
};

const NameCell = (props: GridCustomCellProps) => {
  return <td {...props.tdProps}>{props.dataItem[props.field || ""]}</td>;
};

const NameEditCell = (props: GridCustomCellProps) => {
  const { setTags } = useContext(GridEditContext);
  const [value, setValue] = useState(props.dataItem[props.field || ""] || "");
  const [visited, setVisited] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");

  const field = props.field || "";

  const validateField = (val: string) => {
    const error = requiredValidator(val);
    setValidationMessage(error);
    return error;
  };

  const handleChange = (e: any) => {
    const newValue = e.value;
    setValue(newValue);

    // Update the dataItem directly
    props.dataItem[field] = newValue;

    // Update the grid data
    setTags((prevTags) =>
      prevTags.map((tag) =>
        tag.id === props.dataItem.id ? { ...tag, [field]: newValue } : tag,
      ),
    );

    // Validate on change if field has been visited
    if (visited) {
      validateField(newValue);
    }
  };

  const handleBlur = () => {
    setVisited(true);
    validateField(value);
  };

  // Create field render props for TextInputWithValidation
  const fieldRenderProps: FieldRenderProps = {
    value: value,
    onChange: handleChange,
    onBlur: handleBlur,
    onFocus: () => {},
    validationMessage: validationMessage,
    visited: visited,
    touched: visited,
    modified: false,
    valid: !validationMessage,
    name: field,
    id: field,
    children: null,
  };

  return (
    <td {...props.tdProps}>
      <TextInputWithValidation {...fieldRenderProps} />
    </td>
  );
};

const CommandCell = (props: GridCustomCellProps) => {
  const {
    onDataAction,
    setEdit,
    isLoading,
    setTags,
    setErrorMessage,
    loadTags,
  } = useContext(GridEditContext);
  const isInEdit = props.isInEdit;
  const isNewItem = props.dataItem[DATA_ITEM_KEY] === null;
  const originalValue = props.dataItem.tag;

  const onSaveClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      // Validate the tag field before saving
      const tagValue = (props.dataItem.tag || "").trim();
      const validationError = requiredValidator(tagValue);

      if (validationError) {
        // Show validation error
        setErrorMessage(validationError);
        return;
      }

      // Clear any previous validation errors
      setErrorMessage("");

      // Update the dataItem with trimmed value
      const updatedDataItem = { ...props.dataItem, tag: tagValue };

      // Directly submit the current dataItem since the form data is already bound
      onDataAction({ operation: "save", dataItem: updatedDataItem });
    },
    [onDataAction, props.dataItem, setErrorMessage],
  );

  const onRemoveClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      onDataAction({
        operation: "remove",
        dataItem: props.dataItem,
      });
    },
    [onDataAction, props.dataItem],
  );

  const onEditClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setEdit({ [props.dataItem[DATA_ITEM_KEY]]: true });
    },
    [setEdit, props.dataItem],
  );

  const onDiscardClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      console.log("onDiscardClick", props.dataItem);
      console.log("originalValue", originalValue);
      if (isNewItem) {
        // If it's a new item, remove it from the grid data and exit edit mode
        setTags((data) => data.filter((item) => item !== props.dataItem));
        setEdit({});
      } else {
        // If it's an existing item, just exit edit mode
        setEdit({});
        loadTags();
      }
    },
    [setEdit, isNewItem, props.dataItem],
  );

  const buttonText = isInEdit ? (isNewItem ? "Save" : "Save") : "Edit";
  const buttonIconName = isInEdit ? "bi bi-save" : "bi bi-pencil-fill";
  const buttonBootstrapClass = isInEdit ? "btn-primary" : "btn-secondary";

  return (
    <td {...props.tdProps} className="k-command-cell">
      <button
        type="button"
        className={`btn ${buttonBootstrapClass} btn-sm me-2`}
        onClick={isInEdit ? onSaveClick : onEditClick}
        disabled={isLoading}
      >
        <i className={buttonIconName}></i>
        &nbsp;{buttonText}
      </button>

      {isInEdit && (
        <button
          type="button"
          className={`btn btn-secondary btn-sm me-2`}
          onClick={onDiscardClick}
          disabled={isLoading}
        >
          <i className="bi bi-x-square"></i>
          &nbsp;{isNewItem ? "Cancel" : "Discard"}
        </button>
      )}
      {!isInEdit && (
        <button
          type="button"
          className="btn btn-danger btn-sm me-2"
          onClick={onRemoveClick}
          disabled={isLoading}
        >
          <i className="bi bi-trash"></i>
          &nbsp;Remove
        </button>
      )}
    </td>
  );
};

const CustomRow = (props: GridCustomRowProps) => {
  return (
    <GridInlineFormRow dataItem={props.dataItem} isInEdit={props.isInEdit}>
      <tr {...props.trProps}>{props.children}</tr>
    </GridInlineFormRow>
  );
};

export const AccountTags = () => {
  const [tags, setTags] = useState<TagData[]>([]);
  const [edit, setEdit] = useState<EditDescriptor>({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Load Bootstrap icons CSS for this island component
  useEffect(() => {
    const linkId = "bootstrap-icons-accounts-tags-modal";

    // Check if the link already exists
    if (!document.getElementById(linkId)) {
      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.href =
        "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.5/font/bootstrap-icons.css";
      document.head.appendChild(link);
    }

    // Cleanup function to remove the CSS when component unmounts
    return () => {
      const existingLink = document.getElementById(linkId);
      if (existingLink) {
        document.head.removeChild(existingLink);
      }
    };
  }, []);

  // Load tags on component mount
  const loadTags = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const response = await callApi(
        "sysadmin/accounts/GetTags",
        HttpMethod.GET,
      );
      if (response && Array.isArray(response)) {
        setTags(response);
      } else if (response && response.data && Array.isArray(response.data)) {
        setTags(response.data);
      }
    } catch (error) {
      console.error("Failed to load tags:", error);
      setErrorMessage("Failed to load tags. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  const handleApiResponse = useCallback((response: ApiResponse) => {
    switch (response.type) {
      case "success":
        setSuccessMessage(response.message);
        setErrorMessage("");
        // Auto-hide success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
        break;
      case "error":
        setErrorMessage(response.message);
        setSuccessMessage("");
        break;
    }
  }, []);

  const onDataAction = useCallback(
    async (event: { operation: "save" | "remove" | "add"; dataItem?: any }) => {
      setIsLoading(true);
      setErrorMessage("");
      setSuccessMessage("");

      try {
        switch (event.operation) {
          case "remove":
            const deleteResponse = await callApi(
              `sysadmin/accounts/DeleteTag/${event.dataItem[DATA_ITEM_KEY]}`,
              HttpMethod.DELETE,
            );
            if (deleteResponse && deleteResponse.type === "success") {
              setTags((data) =>
                data.filter(
                  (item) =>
                    item[DATA_ITEM_KEY] !== event.dataItem[DATA_ITEM_KEY],
                ),
              );
              handleApiResponse(deleteResponse);
            } else if (deleteResponse) {
              console.log(deleteResponse);
              handleApiResponse(deleteResponse);
            }
            break;

          case "save":
            const tag: TagData = {
              id: event.dataItem.id,
              tag: event.dataItem.tag,
            };

            const response = await callApi(
              "sysadmin/accounts/UpsertTag",
              HttpMethod.POST,
              tag,
            );

            if (response && response.type === "success") {
              loadTags();
              setEdit({});
              handleApiResponse(response);
            } else if (response) {
              handleApiResponse(response);
            }
            break;

          case "add": {
            setTags((data) => [event.dataItem, ...data]);
            setEdit({ [event.dataItem[DATA_ITEM_KEY]]: true });
            break;
          }

          default:
            break;
        }
      } catch (error) {
        console.log("API operation failed:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiResponse],
  );

  const onAddClick = useCallback(() => {
    onDataAction({
      operation: "add",
      dataItem: { id: null, tag: "" },
    });
  }, [onDataAction]);

  return (
    <GridEditContext.Provider
      value={{
        onDataAction,
        edit,
        setEdit,
        isLoading,
        errorMessage,
        successMessage,
        setTags,
        setErrorMessage,
        loadTags,
      }}
    >
      <div className="p-3">
        {errorMessage && (
          <div className="alert alert-danger mb-3" role="alert">
            {errorMessage}
          </div>
        )}
        {successMessage && (
          <div className="alert alert-success mb-3" role="alert">
            {successMessage}
          </div>
        )}
        <Grid
          data={tags}
          dataItemKey={DATA_ITEM_KEY}
          edit={edit}
          editable={{ mode: "inline" }}
          rows={{ data: CustomRow }}
          className="mt-3"
        >
          <GridToolbar>
            <button
              type="button"
              className="btn btn-primary btn-sm me-2"
              onClick={onAddClick}
              disabled={isLoading}
            >
              <i className="bi bi-plus-square"></i>
              &nbsp;Add new tag
            </button>
          </GridToolbar>
          <GridColumn
            title="Tag Name"
            field="tag"
            cells={{
              data: NameCell,
              edit: { text: NameEditCell },
            }}
          />
          <GridColumn title="Actions" cells={{ data: CommandCell }} />
        </Grid>
      </div>
    </GridEditContext.Provider>
  );
};

export default AccountTags;
